/**
 * Header stat cards for the GHL Qualifier dashboard. Four display modes:
 *  - "overview": top-level rollup across all locations
 *                (Qualified / Converted / In Flight + Claude Spend / Whisper Spend / $ Written).
 *  - "qualified": qualified-leads tab (Total / Qualified / Unqualified +
 *                 Pending / Avg Confidence / By Pattern).
 *  - "converted": converted-leads tab (Confirmed / In Progress / Exhausted +
 *                 $ Written / Avg Confirm Day / Cancelled).
 *  - "location-spend": per-location detail page header
 *                      (Claude Spend / Whisper Spend) — 2-up.
 *
 * All four modes consume the unified `QualifierStats` shape from the
 * `get_ghl_qualifier_stats` RPC.
 */

import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { formatCurrency } from '@/lib/ghl-qualifier'
import {
  calculateTotalCost,
  formatCost,
} from '@/lib/ghl-dashboard'
import type { QualifierStats } from '@/hooks/useGHLQualifierDashboard'

interface Props {
  readonly mode: 'overview' | 'qualified' | 'converted' | 'location-spend'
  readonly stats: QualifierStats | null | undefined
  readonly isLoading?: boolean
}

function formatTokens(tokens: number): string {
  if (tokens === 0) return '0'
  if (tokens < 1000) return String(tokens)
  if (tokens < 1_000_000) return `${(tokens / 1000).toFixed(1)}K`
  return `${(tokens / 1_000_000).toFixed(2)}M`
}

function StatCard({
  title,
  value,
  hint,
  secondary,
  isLoading,
}: {
  title: string
  value: string | number
  hint?: string
  /**
   * Optional secondary stat shown as a small "X of Y" line below the main
   * number. Use this when a card's headline value is a subset of a larger
   * population (e.g. "12 qualified of 25 total") so the label stays honest
   * even when some rows are still pending.
   */
  secondary?: {
    readonly value: string | number
    readonly label: string
  }
  isLoading?: boolean
}) {
  return (
    <Card>
      <CardContent className="pt-6">
        <p className="text-xs text-muted-foreground uppercase tracking-wide">{title}</p>
        {isLoading ? (
          <Skeleton className="h-8 w-24 mt-2" />
        ) : (
          <p className="mt-2 text-2xl font-bold">{value}</p>
        )}
        {secondary && !isLoading ? (
          <p className="mt-1.5 text-xs text-muted-foreground">
            <span className="font-medium tabular-nums text-foreground/80">
              {secondary.value}
            </span>{' '}
            {secondary.label}
          </p>
        ) : null}
        {hint ? <p className="mt-1 text-xs text-muted-foreground">{hint}</p> : null}
      </CardContent>
    </Card>
  )
}

function PatternListCard({
  byPattern,
  isLoading,
}: {
  byPattern: Record<string, number>
  isLoading?: boolean
}) {
  // Sort descending by count and show the top 4 so the card stays compact.
  const sorted = Object.entries(byPattern).sort(([, a], [, b]) => b - a).slice(0, 4)

  return (
    <Card>
      <CardContent className="pt-6">
        <p className="text-xs text-muted-foreground uppercase tracking-wide">By Pattern</p>
        {isLoading ? (
          <div className="mt-2 space-y-1.5">
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-full" />
          </div>
        ) : sorted.length === 0 ? (
          <p className="mt-2 text-sm text-muted-foreground">No patterns yet</p>
        ) : (
          <ul className="mt-2 space-y-1 text-sm">
            {sorted.map(([pattern, count]) => (
              <li key={pattern} className="flex items-center justify-between">
                <span className="text-muted-foreground truncate" title={pattern}>
                  {pattern}
                </span>
                <span className="font-medium tabular-nums">{count}</span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}

export function QualifierStatCards({ mode, stats, isLoading }: Props) {
  if (mode === 'overview') {
    const q = stats?.qualified
    const c = stats?.converted
    const w = stats?.whisper
    const inFlight = (q?.pending ?? 0) + (c?.in_progress ?? 0)
    // Claude pricing comes from the shared util. Token sums now cover both
    // qualified-lead and converted-lead Claude calls (see migration
    // 20260522120000) — the dollar value is the operator-facing translation
    // of those token totals.
    const claudeCost = calculateTotalCost(
      q?.total_input_tokens ?? 0,
      q?.total_output_tokens ?? 0
    )
    const whisperMinutes = Math.round((w?.total_duration_seconds ?? 0) / 60)

    return (
      <div className="grid gap-4 md:grid-cols-3">
        <StatCard
          title="Qualified Leads"
          value={(q?.qualified ?? 0).toLocaleString()}
          secondary={{
            value: `of ${(q?.total ?? 0).toLocaleString()}`,
            label: 'total processed',
          }}
          isLoading={isLoading}
        />
        <StatCard
          title="Converted Leads"
          value={(c?.completed_converted ?? 0).toLocaleString()}
          secondary={{
            value: `of ${(c?.total ?? 0).toLocaleString()}`,
            label: 'total lifecycles',
          }}
          isLoading={isLoading}
        />
        <StatCard
          title="In Flight"
          value={inFlight.toLocaleString()}
          hint="Pending qualifications + open lifecycles"
          isLoading={isLoading}
        />
        <StatCard
          title="Claude Spend"
          value={formatCost(claudeCost)}
          hint={`${formatTokens(q?.total_input_tokens ?? 0)} in / ${formatTokens(q?.total_output_tokens ?? 0)} out`}
          isLoading={isLoading}
        />
        <StatCard
          title="Whisper Spend"
          value={formatCost(w?.total_cost_usd ?? 0)}
          hint={whisperMinutes > 0 ? `${whisperMinutes} min transcribed` : 'No transcriptions yet'}
          isLoading={isLoading}
        />
        <StatCard
          title="$ Written to Opps"
          value={formatCurrency(c?.monetary_value_total ?? 0)}
          hint={
            c?.monetary_value_avg
              ? `Avg ${formatCurrency(c.monetary_value_avg)}`
              : undefined
          }
          isLoading={isLoading}
        />
      </div>
    )
  }

  if (mode === 'location-spend') {
    // Slim 2-card row used above the tabs on the per-location detail page.
    // Same data sources as the overview-mode spend cards but scoped to a
    // single location (the RPC's p_location_id filter handles that).
    const q = stats?.qualified
    const w = stats?.whisper
    const claudeCost = calculateTotalCost(
      q?.total_input_tokens ?? 0,
      q?.total_output_tokens ?? 0
    )
    const whisperMinutes = Math.round((w?.total_duration_seconds ?? 0) / 60)

    return (
      <div className="grid gap-4 md:grid-cols-3">
        <StatCard
          title="Claude Spend"
          value={formatCost(claudeCost)}
          hint={`${formatTokens(q?.total_input_tokens ?? 0)} in / ${formatTokens(q?.total_output_tokens ?? 0)} out`}
          isLoading={isLoading}
        />
        <StatCard
          title="Whisper Spend"
          value={formatCost(w?.total_cost_usd ?? 0)}
          hint={whisperMinutes > 0 ? `${whisperMinutes} min transcribed` : 'No transcriptions yet'}
          isLoading={isLoading}
        />
      </div>
    )
  }

  if (mode === 'qualified') {
    const q = stats?.qualified

    return (
      <div className="grid gap-4 md:grid-cols-3">
        <StatCard
          title="Total"
          value={(q?.total ?? 0).toLocaleString()}
          isLoading={isLoading}
        />
        <StatCard
          title="Qualified"
          value={(q?.qualified ?? 0).toLocaleString()}
          isLoading={isLoading}
        />
        <StatCard
          title="Unqualified"
          value={(q?.unqualified ?? 0).toLocaleString()}
          isLoading={isLoading}
        />
        <StatCard
          title="Pending"
          value={(q?.pending ?? 0).toLocaleString()}
          isLoading={isLoading}
        />
        <StatCard
          title="Avg Confidence"
          value={q?.avg_confidence ? `${q.avg_confidence}` : '—'}
          isLoading={isLoading}
        />
        <PatternListCard byPattern={q?.by_pattern ?? {}} isLoading={isLoading} />
      </div>
    )
  }

  // mode === 'converted'
  const c = stats?.converted

  return (
    <div className="grid gap-4 md:grid-cols-3">
      <StatCard
        title="Confirmed"
        value={(c?.completed_converted ?? 0).toLocaleString()}
        isLoading={isLoading}
      />
      <StatCard
        title="In Progress"
        value={(c?.in_progress ?? 0).toLocaleString()}
        isLoading={isLoading}
      />
      <StatCard
        title="Exhausted (8d)"
        value={(c?.completed_exhausted ?? 0).toLocaleString()}
        isLoading={isLoading}
      />
      <StatCard
        title="$ Written"
        value={formatCurrency(c?.monetary_value_total ?? 0)}
        isLoading={isLoading}
      />
      <StatCard
        title="Avg Confirm Day"
        value={c?.avg_checks_to_confirm ? `${c.avg_checks_to_confirm}` : '—'}
        isLoading={isLoading}
      />
      <StatCard
        title="Cancelled"
        value={(c?.cancelled ?? 0).toLocaleString()}
        hint={c?.failed ? `${c.failed} failed` : undefined}
        isLoading={isLoading}
      />
    </div>
  )
}
